import { Skeleton } from 'antd'
import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { cx } from '@/Ship/Helpers/cssClass'
import type { DashboardAttention, SourceSplit } from '@/Ship/Contracts/DashboardSummary'
import { REQUEST_STATUS_MAP } from '@/Ship/Contracts/Statuses/RequestStatus'
import { TASK_STATUS_MAP } from '@/Ship/Contracts/Statuses/TaskStatus'

import { DELAY_STATUS_MAP } from '@/Containers/AssignmentSection/AssignmentContainer/Contracts/DelayStatus'

import {
    requestsUrl,
    TASK_SOURCE_LABELS,
    tasksUrl,
    type LinkDispatcher,
    type TaskSource,
} from '../Helpers/dashboardLinks'

import styles from './Dashboard.module.css'

/** «Без водія» / «Без ТЗ» — не статус, а прогалина в призначенні: помаранчевий попередження */
const UNASSIGNED_COLOR = '#fa8c16'

interface AttentionTilesProps {
    attention: DashboardAttention | null
    loading: boolean
    dispatcher: LinkDispatcher | null
}

interface TileProps {
    label: string
    value: number
    color: string
    loading: boolean
    note?: string | null
    children?: ReactNode
}

const Tile = ({ label, value, color, loading, note, children }: TileProps) => {
    const muted = !loading && value === 0
    return (
        <div
            className={`${cx(styles, 'tile')} ${muted ? cx(styles, 'tileMuted') : ''}`}
            style={{ '--tile-color': color } as CSSProperties}
        >
            <span className={cx(styles, 'tileLabel')}>{label}</span>
            {loading ? (
                <Skeleton.Input active size="small" style={{ width: 56, minWidth: 56, height: 32 }} />
            ) : (
                <span className={cx(styles, 'tileValue')}>{value}</span>
            )}
            {!loading && note && <span className={cx(styles, 'tileNote')}>{note}</span>}
            {!loading && value > 0 && <div className={cx(styles, 'tileLinks')}>{children}</div>}
        </div>
    )
}

/** Посилання «з заявками N · без заявок M» на дві сторінки задач; нульові не показуються */
const SplitLinks = ({
    label,
    split,
    conditions,
    dispatcher,
}: {
    /** Назва плитки — у aria-label посилання, бо «з заявками 4» без неї ні про що */
    label: string
    split: SourceSplit
    conditions: Record<string, string>
    dispatcher: LinkDispatcher | null
}) => (
    <>
        {(['requests', 'standalone'] as TaskSource[])
            .filter((source) => split[source] > 0)
            .map((source) => (
                <Link
                    key={source}
                    className={cx(styles, 'miniLink')}
                    to={tasksUrl(source, conditions, dispatcher)}
                    aria-label={`${label}, ${TASK_SOURCE_LABELS[source]}: ${split[source]}`}
                >
                    {TASK_SOURCE_LABELS[source]} <b>{split[source]}</b>
                </Link>
            ))}
    </>
)

const EMPTY_SPLIT: SourceSplit = { total: 0, requests: 0, standalone: 0 }

/**
 * «Потребує уваги»: що диспетчеру зробити зараз. Кожна плитка — число і переходи у
 * відфільтрований список (правила ті самі, що в лічильника); нуль — приглушена плитка.
 * Кольори — зі статусних мап, щоб збігалися з бейджами у списках.
 */
export const AttentionTiles = ({ attention, loading, dispatcher }: AttentionTilesProps) => {
    const delays = attention?.delays ?? { ...EMPTY_SPLIT, escalated: 0 }

    return (
        <div className={cx(styles, 'attention')}>
            <Tile
                label="Нові заявки"
                value={attention?.newRequests ?? 0}
                color={REQUEST_STATUS_MAP.new.color}
                loading={loading}
            >
                <Link className={cx(styles, 'miniLink')} to={requestsUrl('new', dispatcher)}>
                    Відкрити заявки →
                </Link>
            </Tile>
            <Tile
                label="Чекають підтвердження"
                value={attention?.awaitingConfirmation.total ?? 0}
                color={TASK_STATUS_MAP.completed_by_driver.color}
                loading={loading}
            >
                <SplitLinks
                    label="Чекають підтвердження"
                    split={attention?.awaitingConfirmation ?? EMPTY_SPLIT}
                    conditions={{ status: 'completed_by_driver' }}
                    dispatcher={dispatcher}
                />
            </Tile>
            <Tile
                label="Затримки без результату"
                value={delays.total}
                color={delays.escalated > 0 ? DELAY_STATUS_MAP.escalated.color : DELAY_STATUS_MAP.received.color}
                note={delays.escalated > 0 ? `з них ${delays.escalated} ескальовано` : null}
                loading={loading}
            >
                <SplitLinks
                    label="Затримки без результату"
                    split={delays}
                    conditions={{ delayStatus: 'received,escalated' }}
                    dispatcher={dispatcher}
                />
            </Tile>
            <Tile
                label="Без водія"
                value={attention?.withoutDriver.total ?? 0}
                color={UNASSIGNED_COLOR}
                loading={loading}
            >
                <SplitLinks
                    label="Без водія"
                    split={attention?.withoutDriver ?? EMPTY_SPLIT}
                    conditions={{ noDriver: '1' }}
                    dispatcher={dispatcher}
                />
            </Tile>
            <Tile
                label="Без ТЗ"
                value={attention?.withoutVehicle.total ?? 0}
                color={UNASSIGNED_COLOR}
                loading={loading}
            >
                <SplitLinks
                    label="Без ТЗ"
                    split={attention?.withoutVehicle ?? EMPTY_SPLIT}
                    conditions={{ noVehicle: '1' }}
                    dispatcher={dispatcher}
                />
            </Tile>
        </div>
    )
}
